// Copyright (C) 2024 Browser Streaming Service for Cloudflare

const { BROWSER_CONFIG } = require('./cloudflare-config');

class BrowserStreamingService {
    constructor() {
        this.useCloudflareWorkers = BROWSER_CONFIG.useCloudflareWorkers;
        this.enableBrowserStreaming = BROWSER_CONFIG.enableBrowserStreaming;
        this.mediaSource = null;
        this.sourceBuffer = null;
        this.videoElement = null;
    }

    // Initialize Media Source Extensions for browser streaming
    initializeMSE(videoElement) {
        if (!BROWSER_CONFIG.streaming.useMSE || !window.MediaSource) {
            console.log('Media Source Extensions not supported');
            return false;
        }

        this.videoElement = videoElement;
        this.mediaSource = new MediaSource();
        
        this.videoElement.src = URL.createObjectURL(this.mediaSource);
        
        this.mediaSource.addEventListener('sourceopen', () => {
            try {
                // Use common video codec
                this.sourceBuffer = this.mediaSource.addSourceBuffer('video/mp4; codecs="avc1.42E01E, mp4a.40.2"');
                this.sourceBuffer.mode = 'sequence';
                console.log('Media Source Extensions initialized successfully');
            } catch (error) {
                console.error('Error initializing MSE:', error);
            }
        });

        return true;
    }

    // Stream video data directly in browser using Cloudflare
    async streamVideo(streamUrl, videoElement) {
        try {
            console.log('Starting browser streaming for URL:', streamUrl);
            
            // Store reference to video element
            this.videoElement = videoElement;
            
            // Check if this is a local streaming server URL
            if (streamUrl.includes('127.0.0.1') || streamUrl.includes('localhost')) {
                console.log('Local streaming server URL detected. Browser streaming cannot handle local server URLs.');
                console.log('This is expected behavior. The streaming server wrapper should handle this case.');
                
                // Return a result indicating that this URL cannot be handled by browser streaming
                return {
                    success: false,
                    error: 'Local streaming server URL cannot be handled by browser streaming',
                    shouldDelegateToWrapper: true
                };
            }

            // Initialize MSE if not already done
            if (!this.mediaSource && !this.initializeMSE(videoElement)) {
                console.log('MSE initialization failed, using direct fallback');
                return this.fallbackStream(streamUrl, videoElement);
            }

            // Check if we should skip Cloudflare Workers for this URL
            if (this.shouldUseDirectFallback(streamUrl)) {
                console.log('Using direct fallback for URL:', streamUrl);
                return this.fallbackStream(streamUrl, videoElement);
            }

            // Use Cloudflare Workers for torrent processing
            if (this.useCloudflareWorkers) {
                const result = await this.streamViaCloudflare(streamUrl);
                if (result.success) {
                    return result;
                } else if (result.fallbackNeeded) {
                    console.log('Cloudflare streaming failed, falling back to direct streaming');
                    return this.fallbackStream(streamUrl, videoElement);
                }
            }

            // Direct streaming fallback
            return await this.fallbackStream(streamUrl, videoElement);
            
        } catch (error) {
            console.error('Browser streaming error:', error);
            console.log('Falling back to direct streaming due to error');
            return this.fallbackStream(streamUrl, videoElement);
        }
    }

    // Stream via Cloudflare Workers
    async streamViaCloudflare(streamUrl) {
        try {
            // Check if this is a local streaming server URL that's not available
            if (streamUrl.includes('127.0.0.1') || streamUrl.includes('localhost')) {
                console.log('Local streaming server not available, using direct fallback');
                return {
                    success: false,
                    error: 'Local streaming server not available',
                    fallbackNeeded: true
                };
            }

            // Create a Cloudflare Worker-compatible stream URL
            const cloudflareStreamUrl = this.createCloudflareStreamUrl(streamUrl);
            
            // For local development or when Cloudflare Workers are not available,
            // try direct streaming first
            if (this.shouldUseDirectFallback(streamUrl)) {
                return {
                    success: false,
                    error: 'Direct streaming needed',
                    fallbackNeeded: true
                };
            }
            
            const response = await fetch(cloudflareStreamUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'video/*,application/*',
                    'Cache-Control': 'no-cache',
                },
                mode: 'cors',
                credentials: 'omit'
            });

            if (!response.ok) {
                throw new Error(`Cloudflare streaming failed: ${response.status}`);
            }

            // Get the video data
            const videoData = await response.arrayBuffer();
            
            // Append to MSE buffer if available
            if (this.sourceBuffer && !this.sourceBuffer.updating) {
                this.sourceBuffer.appendBuffer(videoData);
            }

            return {
                success: true,
                url: cloudflareStreamUrl,
                data: videoData
            };

        } catch (error) {
            console.error('Cloudflare streaming error:', error);
            return {
                success: false,
                error: error.message,
                fallbackNeeded: true
            };
        }
    }

    // Create Cloudflare-compatible stream URL
    createCloudflareStreamUrl(originalUrl) {
        // Handle different types of stream URLs
        
        // If it's already a Cloudflare optimizer URL, use it directly
        const cloudflareOptimizers = [
            'torrentio.strem.fun',
            'comet.elfhosted.com',
            'mediafusion.elfhosted.com',
            'annatar.elfhosted.com',
            'jackettio.elfhosted.com',
            'orionoid.com',
            'stremthru.elfhosted.com'
        ];
        
        const urlObj = new URL(originalUrl);
        if (cloudflareOptimizers.some(domain => urlObj.hostname.includes(domain))) {
            return originalUrl;
        }
        
        // Convert torrent/magnet links to Cloudflare Worker URLs
        if (originalUrl.includes('magnet:') || originalUrl.includes('.torrent')) {
            // Use Comet as the primary Cloudflare Worker
            return `https://comet.elfhosted.com/stream?url=${encodeURIComponent(originalUrl)}`;
        }
        
        // For direct video URLs, check if they need proxying
        if (originalUrl.match(/\.(mp4|webm|ogg|mkv|avi|mov)$/i)) {
            // Check if it's from a CORS-enabled source
            const corsEnabledDomains = [
                'videodelivery.net',
                'cloudflare.com',
                'workers.dev'
            ];
            
            if (corsEnabledDomains.some(domain => urlObj.hostname.includes(domain))) {
                return originalUrl;
            }
            
            // Use Cloudflare Stream for other video URLs
            return `https://videodelivery.net/${encodeURIComponent(originalUrl)}`;
        }

        // Return original URL if no transformation needed
        return originalUrl;
    }

    // Check if we should use direct fallback instead of Cloudflare Workers
    shouldUseDirectFallback(streamUrl) {
        // Skip Cloudflare Workers for:
        // 1. Local streaming server URLs (127.0.0.1, localhost)
        // 2. Direct video URLs (mp4, webm, etc.) from known CORS-problematic sources
        
        const directVideoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
        const url = streamUrl.toLowerCase();
        
        // Check for local streaming server
        if (url.includes('127.0.0.1') || url.includes('localhost')) {
            console.log('Detected local streaming server, using direct fallback');
            return true;
        }
        
        // Check for direct video files from problematic sources
        if (directVideoExtensions.some(ext => url.endsWith(ext))) {
            // Only use direct fallback for video files from non-CORS-enabled sources
            const corsEnabledDomains = [
                'videodelivery.net',
                'cloudflare.com', 
                'workers.dev',
                'stremio.com',
                'stremio.fun'
            ];
            
            const urlObj = new URL(streamUrl);
            const isCorsEnabled = corsEnabledDomains.some(domain => urlObj.hostname.includes(domain));
            
            if (!isCorsEnabled) {
                console.log('Detected direct video file from non-CORS source, using direct fallback');
                return true;
            }
        }
        
        return false;
    }

    // Fallback streaming method
    fallbackStream(streamUrl, videoElement) {
        try {
            console.log('Using fallback streaming for URL:', streamUrl);
            
            // Check if this is a local streaming server URL that's likely to fail
            if (streamUrl.includes('127.0.0.1') || streamUrl.includes('localhost')) {
                console.log('Local streaming server URL detected in fallback. This URL is expected to fail if the server is not running.');
                console.log('The application will continue to work, but video playback requires the local streaming server to be available.');
                
                // For local streaming server URLs, we should not set the video source
                // as it will cause the video element to error when trying to play
                console.log('Skipping video source setup for local streaming server URL to prevent errors');
                
                // Return success but don't set the source - let the streaming server wrapper handle this
                return {
                    success: true,
                    method: 'direct',
                    note: 'Local streaming server URL detected, source not set to prevent errors'
                };
            }
            
            // Clean up any existing MSE
            this.cleanup();
            
            // Clear any existing src first
            if (videoElement.src) {
                URL.revokeObjectURL(videoElement.src);
                videoElement.removeAttribute('src');
                videoElement.load();
            }
            
            // Only set video source for non-local URLs
            if (!streamUrl.includes('127.0.0.1') && !streamUrl.includes('localhost')) {
                videoElement.src = streamUrl;
                videoElement.crossOrigin = 'anonymous';
                
                // Add error handler to video element
                this.videoErrorHandler = (e) => {
                    console.error('Video element error:', e);
                    console.log('Video error details:', {
                        code: videoElement.error?.code,
                        message: videoElement.error?.message,
                        readyState: videoElement.readyState,
                        networkState: videoElement.networkState,
                        src: videoElement.src
                    });
                };
                videoElement.addEventListener('error', this.videoErrorHandler);
                
                console.log('Fallback streaming set up successfully');
            } else {
                console.log('Local streaming server URL detected, video source not set');
            }
            
            return {
                success: true,
                method: 'direct'
            };
            
        } catch (error) {
            console.error('Fallback streaming error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Check browser compatibility
    checkBrowserCompatibility() {
        const features = {
            mediaSource: !!window.MediaSource,
            webRTC: !!window.RTCPeerConnection,
            fetch: !!window.fetch,
            cors: 'withCredentials' in new XMLHttpRequest(),
            serviceWorker: 'serviceWorker' in navigator
        };

        console.log('Browser compatibility check:', features);
        return features;
    }

    // Clean up resources
    cleanup() {
        try {
            // Remove video error handler if exists
            if (this.videoErrorHandler && this.videoElement) {
                this.videoElement.removeEventListener('error', this.videoErrorHandler);
                this.videoErrorHandler = null;
            }
            
            // Stop all source buffer operations
            if (this.sourceBuffer) {
                try {
                    if (this.mediaSource && this.mediaSource.readyState === 'open') {
                        this.mediaSource.endOfStream();
                    }
                } catch (e) {
                    console.warn('Error ending media source stream:', e);
                }
            }
            
            // Remove source buffer
            if (this.sourceBuffer && this.mediaSource) {
                try {
                    this.mediaSource.removeSourceBuffer(this.sourceBuffer);
                } catch (e) {
                    console.warn('Error removing source buffer:', e);
                }
                this.sourceBuffer = null;
            }
            
            // Clear media source
            if (this.mediaSource) {
                try {
                    if (this.mediaSource.readyState === 'open') {
                        this.mediaSource.endOfStream();
                    }
                } catch (e) {
                    console.warn('Error ending media source stream:', e);
                }
                this.mediaSource = null;
            }
            
            // Clear video element reference
            if (this.videoElement && this.videoElement.src) {
                URL.revokeObjectURL(this.videoElement.src);
            }
            this.videoElement = null;
            
            console.log('Browser streaming cleanup completed');
            
        } catch (error) {
            console.error('Error during browser streaming cleanup:', error);
        }
    }
}

// Export singleton instance
module.exports = new BrowserStreamingService();